import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, CircleDollarSign, Mail, Phone, Search, X } from "lucide-react";

import { ApiError, api } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Loader from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import { EmptyPanel, PageHero, PagePanel, PageShell, PanelHeading } from "@/components/interior-page";
import {
  APPLICATION_STATUSES,
  STATUS_STYLE,
  fmtDateTime,
  statusLabel,
  type FundingApplication,
} from "@/lib/funding";
import { cn } from "@/lib/utils";

/* Staff queue for Funding Marketplace submissions.

   The alert email still goes out on every submission; this is where the
   buyer-visible status is moved along (the buyer sees it on their lender card)
   and where staff keep internal notes. An org admin sees only their own
   organization's applications — scoping is enforced server-side. */

const PAGE_SIZE = 20;

interface AdminApplication extends FundingApplication {
  full_name: string | null;
  email: string | null;
  phone: string | null;
  org_id: number;
  user_id: string | null;
  staff_notes: string | null;
  listing_reference: string | null;
  answer_rows: [string, string][];
}

interface ListResponse {
  items: AdminApplication[];
  total: number;
  page: number;
  page_size: number;
  counts: Record<string, number>;
}

function StatusChip({ status }: { status: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold",
        STATUS_STYLE[status] ?? STATUS_STYLE.submitted,
      )}
    >
      {statusLabel(status)}
    </span>
  );
}

function DetailModal({
  app,
  onClose,
  onSaved,
}: {
  app: AdminApplication;
  onClose: () => void;
  onSaved: (a: AdminApplication) => void;
}) {
  const toast = useToast();
  const [status, setStatus] = useState(app.status);
  const [notes, setNotes] = useState(app.staff_notes ?? "");
  const [saving, setSaving] = useState(false);
  const dirty = status !== app.status || notes !== (app.staff_notes ?? "");

  async function save() {
    setSaving(true);
    try {
      const updated = await api.patch<AdminApplication>(`/funding/admin/applications/${app.id}`, {
        status,
        staff_notes: notes,
      });
      onSaved(updated);
      toast.success(status !== app.status ? `Status set to ${statusLabel(status)}. The buyer sees it now.` : "Saved.");
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88dvh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-border/80 bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 border-b border-border/70 px-5 py-4">
          <div className="min-w-0">
            <div className="font-mono text-sm font-bold text-primary">{app.reference_code}</div>
            <div className="mt-0.5 font-bold text-foreground">{app.full_name || app.email || "Unnamed applicant"}</div>
            <div className="text-xs text-muted-foreground">
              {app.partner_name} · {fmtDateTime(app.created_at)}
            </div>
          </div>
          <button onClick={onClose} className="ml-auto shrink-0 rounded-full p-1.5 hover:bg-muted" aria-label="Close">
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
            {app.email && (
              <a href={`mailto:${app.email}`} className="inline-flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5 text-primary" />
                <span className="text-foreground">{app.email}</span>
              </a>
            )}
            {app.phone && (
              <a href={`tel:${app.phone}`} className="inline-flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-primary" />
                <span className="text-foreground">{app.phone}</span>
              </a>
            )}
          </div>

          {app.listing_id && (
            // /listings/:id is a shared route — never org-prefixed.
            <Link
              to={`/listings/${app.listing_id}`}
              className="flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/8 px-3 py-2.5"
            >
              <Briefcase className="h-4 w-4 shrink-0 text-primary" />
              <span className="min-w-0 truncate text-sm text-foreground">
                {app.listing_title ?? "Listing"} {app.listing_reference && `(${app.listing_reference})`}
              </span>
            </Link>
          )}

          <div>
            <p className="mb-2 text-[0.62rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">Answers</p>
            <dl className="divide-y divide-border/50 rounded-xl border border-border/60">
              {app.answer_rows.map(([label, value]) => (
                <div key={label} className="grid grid-cols-1 gap-1 px-3 py-2.5 sm:grid-cols-[1fr_1.2fr] sm:gap-4">
                  <dt className="text-xs text-muted-foreground">{label}</dt>
                  <dd className="whitespace-pre-wrap break-words text-sm font-medium text-foreground">{value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div>
            <p className="mb-2 text-[0.62rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
              Status (shown to the buyer)
            </p>
            <div className="flex flex-wrap gap-2">
              {APPLICATION_STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs font-semibold transition-colors",
                    status === s ? STATUS_STYLE[s] : "border-border/70 text-muted-foreground hover:border-primary/35",
                  )}
                >
                  {statusLabel(s)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-[0.62rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
              Internal notes (never shown to the buyer)
            </p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={5000}
              placeholder="e.g. Duneland confirmed receipt 10/2, waiting on PFS"
              className="w-full rounded-2xl border border-border/80 bg-card/85 px-4 py-3 text-sm leading-relaxed placeholder:text-muted-foreground/70 focus-visible:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border/70 px-5 py-3">
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button type="button" onClick={save} disabled={!dirty || saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminFundingApplicationsPage() {
  const toast = useToast();
  const [data, setData] = useState<ListResponse | null>(null);
  const [fetching, setFetching] = useState(false);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>("");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [selected, setSelected] = useState<AdminApplication | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setAppliedSearch(search.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
    if (status) params.set("status", status);
    if (appliedSearch) params.set("search", appliedSearch);
    api
      .get<ListResponse>(`/funding/admin/applications?${params.toString()}`)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load funding applications");
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, status, appliedSearch, reload, toast]);

  if (!data) return <Loader className="min-h-[60vh]" />;

  const allCount = Object.values(data.counts).reduce((a, b) => a + b, 0);

  return (
    <PageShell>
      <PageHero
        eyebrow="Funding Marketplace"
        title="Funding applications"
        description="Every pre-qualification inquiry buyers have sent to a funding partner. Set the status here and the buyer sees it on their lender card."
        icon={CircleDollarSign}
      />

      <PagePanel>
        <PanelHeading
          title="Applications"
          description={`${data.total.toLocaleString()} ${data.total === 1 ? "application" : "applications"}`}
          actions={
            <div className="relative w-full sm:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, email, phone or reference"
                className="pl-9"
              />
            </div>
          }
        />

        <div className="mb-4 flex flex-wrap gap-2">
          {[["", "All", allCount] as const, ...APPLICATION_STATUSES.map((s) => [s, statusLabel(s), data.counts[s] ?? 0] as const)].map(
            ([value, label, n]) => (
              <button
                key={value || "all"}
                type="button"
                onClick={() => {
                  setStatus(value);
                  setPage(1);
                }}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-semibold transition-colors",
                  status === value
                    ? "border-primary/55 bg-primary/12 text-primary"
                    : "border-border/70 text-muted-foreground hover:border-primary/35",
                )}
              >
                {label} <span className="ml-1 opacity-70">{n.toLocaleString()}</span>
              </button>
            ),
          )}
        </div>

        {data.items.length === 0 ? (
          <EmptyPanel
            icon={CircleDollarSign}
            title={appliedSearch || status ? "No applications match" : "No applications yet"}
            description={
              appliedSearch || status
                ? "Try a different search or status."
                : "Inquiries buyers send from the Funding Marketplace will appear here."
            }
          />
        ) : (
          <div className="relative overflow-x-auto">
            {fetching && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/60 backdrop-blur-[1px]">
                <Loader size="sm" />
              </div>
            )}
            <table className="w-full min-w-[760px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border/80 text-left text-[0.7rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                  <th className="px-3 py-2.5">Reference</th>
                  <th className="px-3 py-2.5">Applicant</th>
                  <th className="px-3 py-2.5">Lender</th>
                  <th className="px-3 py-2.5">Deal</th>
                  <th className="px-3 py-2.5">Submitted</th>
                  <th className="px-3 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((a) => (
                  <tr
                    key={a.id}
                    onClick={() => setSelected(a)}
                    className="cursor-pointer border-b border-border/50 last:border-0 hover:bg-muted/20"
                  >
                    <td className="px-3 py-3 font-mono text-xs font-bold text-foreground">{a.reference_code}</td>
                    <td className="px-3 py-3">
                      <div className="font-semibold text-foreground">{a.full_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{a.email}</div>
                    </td>
                    <td className="px-3 py-3 text-foreground/85">{a.partner_name}</td>
                    <td className="max-w-[14rem] truncate px-3 py-3 text-foreground/85">{a.listing_title ?? "—"}</td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">{fmtDateTime(a.created_at)}</td>
                    <td className="px-3 py-3">
                      <StatusChip status={a.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              page={page}
              totalElements={data.total}
              pageSize={PAGE_SIZE}
              onChange={setPage}
              className="mt-4"
            />
          </div>
        )}
      </PagePanel>

      {selected && (
        <DetailModal
          app={selected}
          onClose={() => setSelected(null)}
          onSaved={(updated) => {
            setSelected(updated);
            // Refetch so the row and the status counts both reflect the change.
            setReload((r) => r + 1);
          }}
        />
      )}
    </PageShell>
  );
}
